import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import {
  addNativeResultListener,
  getOfflineData as postGetOfflineData,
  getOfflineCollection as postGetOfflineCollection,
  getOfflineStatus,
  getSyncStatus,
  isNativeApp,
  notifyWebBridgeReady,
  queueOfflineCommand as postQueueOfflineCommand,
  resolveOfflineConflict,
  syncNow as postSyncNow,
} from "@/lib/hiveflowNative";

const NativeOfflineContext = createContext(null);
const DEFAULT_TIMEOUT = 7000;

const COLLECTION_ALIASES = { stands: "apiaries" };
function requestId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  return `web-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function normalizeStatus(payload = {}, previous = {}) {
  const pending = payload.pendingActions ?? payload.pending_actions ?? payload.pending ?? previous.pendingActions ?? 0;
  const conflicts = payload.conflicts ?? payload.conflictCount ?? payload.conflict_count ?? previous.conflicts ?? 0;
  const phase = payload.phase ?? payload.status ?? payload.syncState ?? payload.sync_state ?? previous.phase ?? "idle";
  const state = String(payload.networkState ?? payload.connectionState ?? payload.state ?? "").toLowerCase();
  const inferredOnline = ["offline", "disconnected", "none", "unavailable"].includes(state)
    ? false
    : ["online", "connected", "available"].includes(state) ? true : undefined;
  const explicitSyncing = payload.syncing ?? payload.isSyncing ?? payload.is_syncing;
  const phaseSyncing = ["syncing", "running", "in_progress"].includes(String(phase).toLowerCase());
  return {
    online: payload.online ?? payload.isOnline ?? payload.isConnected ?? inferredOnline ?? previous.online ?? (typeof navigator === "undefined" ? true : navigator.onLine),
    pendingActions: Number.isFinite(Number(pending)) ? Number(pending) : 0,
    conflicts: Number.isFinite(Number(conflicts)) ? Number(conflicts) : 0,
    syncing: explicitSyncing ?? (phaseSyncing ? true : false),
    phase,
    lastSync: payload.lastSync ?? payload.lastSyncAt ?? payload.last_sync ?? payload.last_sync_at ?? payload.lastSyncedAt ?? payload.last_synced_at ?? previous.lastSync ?? null,
    lastError: payload.error ?? payload.message ?? previous.lastError ?? null,
  };
}

export function shouldRefreshOpenedCollections(type, payload = {}) {
  return type === "OFFLINE_CACHE_READY" || (type === "SYNC_RESULT" && payload.ok !== false && !payload.error);
}

function conflictKey(item = {}) {
  return item.clientActionId ?? item.client_action_id ?? item.conflictId ?? item.conflict_id ?? item.id
    ?? [item.entityType, item.entityId ?? item.entityLocalId, item.reason].filter(Boolean).join(":");
}

export function mergeConflicts(current = [], incoming = []) {
  const merged = new Map();
  for (const item of [...(Array.isArray(current) ? current : []), ...(Array.isArray(incoming) ? incoming : [])]) {
    if (!item || typeof item !== "object") continue;
    const key = conflictKey(item);
    if (!key) continue;
    merged.set(String(key), { ...(merged.get(String(key)) || {}), ...item });
  }
  return [...merged.values()];
}

export function mergeOptimisticEntity(existing, optimistic = {}, nativeOptimistic = {}) {
  return {
    ...(existing || {}),
    ...(optimistic || {}),
    ...(nativeOptimistic || {}),
    _offline: {
      ...(existing?._offline || {}),
      ...(optimistic?._offline || {}),
      ...(nativeOptimistic?._offline || {}),
    },
  };
}

function dataFromPayload(payload = {}) {
  const rawSource = payload.data && typeof payload.data === "object" ? payload.data : payload;
  const entity = payload.entity ?? rawSource.entity;
  const items = payload.items ?? rawSource.items;
  const source = entity && Array.isArray(items)
    ? { [entity === "stands" ? "apiaries" : entity]: items }
    : rawSource;
  const received = entity
    ? [entity === "stands" ? "apiaries" : entity]
    : ["apiaries", "hives", "inspections", "pendingActions"].filter((key) => source[key] !== undefined || (key === "apiaries" && source.stands !== undefined));
  const apiaries = source.apiaries ?? source.stands ?? [];
  const hives = source.hives ?? source.kasten ?? [];
  return {
    apiaries: apiaries.map((stand) => ({
      ...stand,
      id: stand.id ?? stand.standId ?? stand.stand_id,
    })),
    hives: hives.map((hive) => ({
      ...hive,
      id: hive.id ?? hive.hiveId ?? hive.hive_id,
      apiary_id: hive.apiary_id ?? hive.apiaryId ?? hive.standId ?? hive.stand_id,
    })),
    inspections: source.inspections ?? source.inspecties ?? [],
    pendingActions: source.pendingActions ?? source.pending_actions ?? [],
    cachedAt: source.cachedAt ?? source.cached_at ?? payload.timestamp ?? null,
    _received: received,
  };
}

function mergeOfflineData(current, next) {
  const received = new Set(next?._received || []);
  return {
    apiaries: received.has("apiaries") ? next.apiaries : current?.apiaries || [],
    hives: received.has("hives") ? next.hives : current?.hives || [],
    inspections: received.has("inspections") ? next.inspections : current?.inspections || [],
    pendingActions: received.has("pendingActions") ? next.pendingActions : current?.pendingActions || [],
    cachedAt: next.cachedAt || current?.cachedAt || null,
    _received: [...new Set([...(current?._received || []), ...received])],
  };
}

function normalizeOfflineItem(item = {}) {
  const meta = item._offline || {};
  return {
    ...item,
    id: item.id ?? meta.serverId ?? meta.entityId,
    syncStatus: meta.syncStatus ?? item.syncStatus ?? "synced",
    updatedAt: meta.updatedAt ?? item.updatedAt ?? item.updated_at,
    isDeleted: meta.isDeleted ?? item.isDeleted ?? false,
  };
}

export function NativeOfflineProvider({ children }) {
  const { user } = useAuth();
  const nativeApp = isNativeApp();
  const pendingRequests = useRef(new Map());
  const openedCollections = useRef(new Map());
  const collectionsRef = useRef({});
  const refreshOpenedCollectionsRef = useRef(async () => {});
  const [status, setStatus] = useState(() => normalizeStatus());
  const [offlineData, setOfflineData] = useState(null);
  const [collections, setCollections] = useState({});
  const [collectionErrors, setCollectionErrors] = useState({});
  const [notices, setNotices] = useState([]);
  const [conflicts, setConflicts] = useState([]);
  const [cacheRevision, setCacheRevision] = useState(0);

  const settleRequest = useCallback((type, payload = {}) => {
    const id = payload.requestId ?? payload.request_id;
    if (!id) return false;
    const pending = pendingRequests.current.get(id);
    if (!pending) return false;
    if (type !== "NATIVE_ERROR" && pending.expected.size > 0 && !pending.expected.has(type)) return false;
    clearTimeout(pending.timer);
    pendingRequests.current.delete(id);
    if (type === "NATIVE_ERROR" || payload.ok === false || payload.error) pending.reject(new Error(payload.error || payload.message || "Native actie mislukt"));
    else pending.resolve(payload);
    return true;
  }, []);

  useEffect(() => {
    setOfflineData(null);
    setCollections({});
    setCollectionErrors({});
    setNotices([]);
    setConflicts([]);
    openedCollections.current.clear();
  }, [user?.id, user?.organization_id]);

  useEffect(() => {
    if (!nativeApp) return undefined;
    const requests = pendingRequests.current;
    const stop = addNativeResultListener(({ type, payload = {}, requestId: resultRequestId }) => {
      const correlatedPayload = resultRequestId && !payload.requestId && !payload.request_id
        ? { ...payload, requestId: resultRequestId }
        : payload;
      settleRequest(type, correlatedPayload);
      if (type === "OFFLINE_STATUS" || type === "OFFLINE_STATUS_RESULT" || type === "SYNC_STATUS" || type === "SYNC_RESULT") {
        setStatus((current) => normalizeStatus(payload, current));
      }
      if (type === "OFFLINE_CACHE_READY") {
        setStatus((current) => normalizeStatus(payload, current));
      }
      if (type === "OFFLINE_DATA_RESULT" || type === "GET_OFFLINE_DATA_RESULT" || type === "OFFLINE_DATA") {
        setOfflineData((current) => mergeOfflineData(current, dataFromPayload(correlatedPayload)));
      }
      if (type === "OFFLINE_COLLECTION_RESULT") {
        const entityType = correlatedPayload.entityType ?? correlatedPayload.entity;
        if (entityType) {
          setCollections((current) => ({
            ...current,
            [COLLECTION_ALIASES[entityType] || entityType]: (correlatedPayload.items || []).map(normalizeOfflineItem),
          }));
        }
      }
      if (type === "SYNC_NOTICE") {
        setNotices((current) => [...current.slice(-4), { ...payload, receivedAt: new Date().toISOString() }]);
      }
      if (type === "OFFLINE_CONFLICT" || type === "OFFLINE_CONFLICTS_RESULT" || type === "SYNC_CONFLICT") {
        const next = payload.conflicts ?? payload.items ?? [payload];
        setConflicts((current) => mergeConflicts(current, Array.isArray(next) ? next : []));
      }
      if (type === "INSPECTION_OFFLINE_SAVED" || type === "SAVE_INSPECTION_OFFLINE_RESULT" || type === "OFFLINE_ACTION_SAVED") {
        setStatus((current) => normalizeStatus({
          ...current,
          pendingActions: payload.pendingActions ?? payload.pending_actions ?? current.pendingActions + 1,
        }, current));
      }
      if (shouldRefreshOpenedCollections(type, payload)) {
        Promise.resolve().then(() => refreshOpenedCollectionsRef.current());
      }
    });
    getSyncStatus({ requestId: requestId() });
    getOfflineStatus({ requestId: requestId() });
    const readyTimer = setTimeout(() => notifyWebBridgeReady({
      bridgeVersion: 2,
      offlineSyncVersion: 2,
      syncProtocolVersion: 2,
      route: window.location.pathname,
    }), 0);
    return () => {
      clearTimeout(readyTimer);
      stop();
      for (const pending of requests.values()) {
        clearTimeout(pending.timer);
        pending.reject(new Error("Native bridge gesloten"));
      }
      requests.clear();
    };
  }, [nativeApp, settleRequest]);

  useEffect(() => {
    if (!nativeApp) return undefined;
    const onOnline = () => setStatus((current) => ({ ...current, online: true }));
    const onOffline = () => setStatus((current) => ({ ...current, online: false }));
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, [nativeApp]);

  const sendRequest = useCallback((send, payload, expected, timeout = DEFAULT_TIMEOUT) => {
    if (!nativeApp) return Promise.reject(new Error("Native offline-opslag is niet beschikbaar"));
    const id = requestId();
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        pendingRequests.current.delete(id);
        reject(new Error("Geen antwoord van de offline-opslag"));
      }, timeout);
      pendingRequests.current.set(id, { resolve, reject, timer, expected: new Set(expected) });
      if (!send({ ...payload, requestId: id, userId: user?.id, tenantId: user?.organization_id || user?.id })) {
        clearTimeout(timer);
        pendingRequests.current.delete(id);
        reject(new Error("Native bridge is niet beschikbaar"));
      }
    });
  }, [nativeApp, user]);

  const loadOfflineData = useCallback(async (options = {}) => {
    const entities = options.entities || ["stands", "hives", "inspections"];
    const results = await Promise.all(entities.map((entity) => sendRequest(
      postGetOfflineData,
      { entity, filters: options.filters || {}, reason: options.reason },
      ["OFFLINE_DATA_RESULT", "GET_OFFLINE_DATA_RESULT", "OFFLINE_DATA"],
    )));
    const data = results.reduce((current, result) => mergeOfflineData(current, dataFromPayload(result)), null);
    setOfflineData(data);
    return data;
  }, [sendRequest]);

  const getCollection = useCallback(async (entityType, options = {}) => {
    const trackedOptions = {
      entityId: options.entityId,
      parentId: options.parentId,
      filters: options.filters || {},
      includeDeleted: options.includeDeleted === true,
      onlyDeleted: options.onlyDeleted === true,
      limit: options.limit || 500,
    };
    openedCollections.current.set(`${entityType}:${JSON.stringify(trackedOptions)}`, { entityType, options: trackedOptions });
    try {
      const result = await sendRequest(
        postGetOfflineCollection,
        {
          entityType,
          entityId: options.entityId,
          parentId: options.parentId,
          filters: options.filters || {},
          includeDeleted: options.includeDeleted === true,
          onlyDeleted: options.onlyDeleted === true,
          limit: options.limit || 500,
        },
        ["OFFLINE_COLLECTION_RESULT"],
      );
      const items = (result.items || []).map(normalizeOfflineItem);
      setCollections((current) => ({ ...current, [COLLECTION_ALIASES[entityType] || entityType]: items }));
      setCollectionErrors((current) => ({ ...current, [entityType]: null }));
      return items;
    } catch (error) {
      setCollectionErrors((current) => ({ ...current, [entityType]: error.message || "Offline-opslag niet beschikbaar" }));
      throw error;
    }
  }, [sendRequest]);

  const refreshOpenedCollections = useCallback(async () => {
    const requests = [...openedCollections.current.values()];
    if (requests.length === 0) return [];
    const results = await Promise.allSettled(requests.map(({ entityType, options }) => getCollection(entityType, options)));
    setCacheRevision((current) => current + 1);
    window.dispatchEvent(new CustomEvent("hiveflow:offline-cache-ready", {
      detail: { collections: requests.map(({ entityType }) => entityType), refreshedAt: new Date().toISOString() },
    }));
    return results;
  }, [getCollection]);

  useLayoutEffect(() => {
    collectionsRef.current = collections;
    refreshOpenedCollectionsRef.current = refreshOpenedCollections;
    return () => { refreshOpenedCollectionsRef.current = async () => {}; };
  }, [collections, refreshOpenedCollections]);

  const queueCommand = useCallback(async (command) => {
    const clientActionId = command.clientActionId || requestId();
    const entityId = command.entityId || command.optimisticEntity?.id || `local-${requestId()}`;
    const key = COLLECTION_ALIASES[command.entityType] || command.entityType;
    const existing = (collectionsRef.current[key] || []).find((item) => (
      String(item.id) === String(entityId)
      || (command.entityServerId && String(item.id) === String(command.entityServerId))
    ));
    const optimisticEntity = normalizeOfflineItem(mergeOptimisticEntity(existing, {
      ...(command.payload || {}),
      ...(command.optimisticEntity || {}),
      id: entityId,
      _offline: {
        ...(existing?._offline || {}),
        ...(command.optimisticEntity?._offline || {}),
        entityId,
        serverId: command.entityServerId || null,
        syncStatus: "pending",
        updatedAt: new Date().toISOString(),
        isDeleted: command.operation === "delete",
      },
    }));
    const result = await sendRequest(
      postQueueOfflineCommand,
      {
        ...command,
        clientActionId,
        entityId,
        operation: command.operation || "upsert",
        optimisticEntity,
      },
      ["OFFLINE_COMMAND_QUEUED"],
    );
    const acceptedEntity = normalizeOfflineItem(mergeOptimisticEntity(existing, optimisticEntity, result.optimisticEntity));
    setCollections((current) => {
      const existing = current[key] || [];
      const next = existing.filter((item) => item.id !== entityId);
      if (command.operation !== "delete") next.unshift(acceptedEntity);
      return { ...current, [key]: next };
    });
    setStatus((current) => normalizeStatus({ ...current, pendingActions: current.pendingActions + 1 }, current));
    return { ...result, clientActionId, entityId, optimisticEntity: acceptedEntity };
  }, [sendRequest]);

  const saveInspection = useCallback(async (inspection) => {
    const localId = `local-${requestId()}`;
    const result = await queueCommand({
      actionType: "CREATE_INSPECTION",
      entityType: "inspections",
      entityId: localId,
      entityServerId: null,
      operation: "upsert",
      payload: inspection,
      optimisticEntity: { ...inspection, id: localId },
    });
    setOfflineData((current) => current ? {
      ...current,
      inspections: [result.optimisticEntity, ...(current.inspections || []).filter((item) => item.id !== localId)],
    } : current);
    return result;
  }, [queueCommand]);

  const resolveConflict = useCallback(async (clientActionId, strategy) => {
    if (!clientActionId || !["accept_server", "retry_local"].includes(strategy)) {
      throw new Error("Ongeldige conflictkeuze");
    }
    const result = await sendRequest(resolveOfflineConflict, {
      clientActionId,
      strategy,
    }, ["RESOLVE_OFFLINE_CONFLICT_RESULT"]);
    if (result.ok === false || result.resolved === false) throw new Error(result.message || "Conflictkeuze is afgewezen");
    setConflicts((current) => current.filter((item) => (item.clientActionId ?? item.client_action_id) !== clientActionId));
    return true;
  }, [sendRequest]);

  const syncNow = useCallback(() => {
    if (!nativeApp) return false;
    setStatus((current) => ({ ...current, syncing: true, phase: "syncing" }));
    return postSyncNow({ requestId: requestId() });
  }, [nativeApp]);

  const refreshStatus = useCallback(() => {
    if (!nativeApp) return false;
    const syncSent = getSyncStatus({ requestId: requestId() });
    const offlineSent = getOfflineStatus({ requestId: requestId() });
    return syncSent || offlineSent;
  }, [nativeApp]);

  useLayoutEffect(() => {
    if (!nativeApp) return undefined;
    window.__hiveflowOfflineRepository = { nativeApp: true, online: status.online, getCollection, queueCommand };
    return () => { delete window.__hiveflowOfflineRepository; };
  }, [getCollection, nativeApp, queueCommand, status.online]);

  const value = useMemo(() => ({
    nativeApp,
    online: nativeApp ? status.online : (typeof navigator === "undefined" ? true : navigator.onLine),
    status,
    offlineData,
    collections,
    collectionErrors,
    notices,
    conflicts,
    cacheRevision,
    loadOfflineData,
    getCollection,
    queueCommand,
    saveInspection,
    syncNow,
    refreshStatus,
    resolveConflict,
  }), [cacheRevision, collectionErrors, collections, conflicts, getCollection, loadOfflineData, nativeApp, notices, offlineData, queueCommand, refreshStatus, resolveConflict, saveInspection, status, syncNow]);

  return <NativeOfflineContext.Provider value={value}>{children}</NativeOfflineContext.Provider>;
}

export function useNativeOffline() {
  const context = useContext(NativeOfflineContext);
  if (!context) throw new Error("useNativeOffline moet binnen NativeOfflineProvider worden gebruikt");
  return context;
}
